import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';
import type { RootState } from 'js/src/libs/RootState';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    deleteLuminaryOverlayOperation,
    deleteSceneGroupOperation,
} from 'js/src/luminary/luminary-operations';
import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';

export const deleteSceneSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
});
export type DeleteSceneArgs = z.infer<typeof deleteSceneSchema>;

export class DeleteSceneTool extends VideoStateTool<typeof deleteSceneSchema> {
    protected override readonly schema = deleteSceneSchema;
    protected override readonly toolName = TOOL_NAMES.deleteScene;
    protected override readonly toolDescription = 'Delete a scene from the video';

    public override getToolDisplayName(): string {
        return 'Deleting scene';
    }

    public get statusIcon(): string {
        return 'delete';
    }

    public async execute(args: DeleteSceneArgs, context: ToolExecutionContext) {
        const { parentActionGroup } = context;

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];
            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup) {
                return `Error: No scene group found for scene ${sceneShortId}`;
            }

            // The Lumen5 branding scene is required on the free plan and can only be
            // removed by users whose plan allows removing branding.
            if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
                const canRemoveBranding = SERVER?.USER?.capabilities?.no_branding ?? false;
                if (!canRemoveBranding) {
                    return `Error: This is the Lumen5 branding scene, which is required on the free plan and cannot be deleted. Use the ${TOOL_NAMES.suggestUpgrade} tool (feature "remove_branding") to let the user upgrade to remove Lumen5 branding.`;
                }
            }

            // Check if this is a voice-driven video (talking head or voiceover)
            if (isVoiceDrivenSceneGroup(sceneGroup)) {
                if (!isSceneAnOverlay(this.getState(), scene)) {
                    return `Error: Scene ${sceneShortId} is the base/presenter scene of a voice-driven scene group, which carries the voice-over and controls the group's timing, so it can't be deleted. To change what shows here, re-time an overlay with ${TOOL_NAMES.setSceneRange} or add one with ${TOOL_NAMES.addVoiceoverScene}. To remove this whole section of the video, including its voice-over, delete the entire scene group with ${TOOL_NAMES.deleteSceneGroup}.`;
                }

                try {
                    const { deleteLuminaryOverlay } = makeOperationsCallable(
                        { deleteLuminaryOverlayOperation },
                        this.luminary,
                        this.dispatch,
                        this.getState as () => RootState,
                    );

                    // fillGap = false leaves the span as a gap for the agent to re-cover.
                    deleteLuminaryOverlay(scene.id, sceneGroup.id, false, parentActionGroup, false);

                    // No scene count: the overlay becomes a base scene in place, so it wouldn't change.
                    return `Success: Deleted scene ${sceneShortId}.`;
                } catch (error) {
                    const errorMessage =
                        error instanceof Error ? error.message : 'Unknown error occurred';
                    return `Error deleting scene: ${errorMessage}`;
                }
            }

            // currently only support deleting scene groups containing a single scene (shouldn't happen due to above
            // voicedriven check)
            if (sceneGroup.scenes.length !== 1) {
                return `Error: Cannot delete scene ${sceneShortId} because it contains multiple scenes.`;
            }

            const sceneGroupId = sceneGroup.id;

            const { deleteSceneGroup } = makeOperationsCallable(
                {
                    deleteSceneGroupOperation,
                },
                this.luminary,
                this.dispatch,
                () => null as unknown as RootState, // NOTE: no state currently
            );

            try {
                // Use the existing deleteSceneGroupOperation which handles validation
                // Pass parentActionGroup to ensure proper undo/redo grouping
                deleteSceneGroup(sceneGroupId, false, parentActionGroup);

                return `Success: Deleted scene ${sceneShortId}. The video now has ${this.luminary.scenes.length} scenes.`;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error deleting scene: ${errorMessage}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error deleting scene: ${errorMessage}`;
        }
    }
}
